import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatePicker from 'components/CustomInputs/DatePicker';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { AppContext } from 'context';
import { useEligibleStreams } from 'hooks';
import { Client, SortingCenterContractFormType } from 'types';
import IntegerField from 'components/CustomInputs/IntegerField';
import { useLatestClosureDate } from 'utils/closureHooks';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { sortingCenterContractFormSchema } from './SortingCenterContractForm.yup';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
  fieldLine: {
    marginTop: '8px',

    '& > div': {
      minWidth: '470px',
    },

    '& span': {
      fontSize: 18,
      marginBottom: '5px',
    },
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: '214px',
    },
  },
});

interface SortingCenterContractFormProps {
  onSubmit: SubmitHandler<SortingCenterContractFormType>;
  formName: string;
  client: Client;
}

const SortingCenterContractForm: React.FC<SortingCenterContractFormProps> = ({ onSubmit, formName, client }) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'sortingCenterContracts.create' });

  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const [lastClosureDate] = useLatestClosureDate(sortingCenter);

  const schema = useMemo(() => sortingCenterContractFormSchema(t, lastClosureDate ?? new Date()), [t, lastClosureDate]);

  const { control, handleSubmit, register, trigger } = useForm<SortingCenterContractFormType>({
    mode: 'onBlur',
    defaultValues: {
      startDate: null,
      endDate: null,
      stream: '',
      characterizationNumber: null,
      estimatedReceptionWeight: null,
    },
    resolver: yupResolver(schema),
  });

  const [streamChoices] = useEligibleStreams(transformObjectId(client));

  return (
    <form id={formName} onSubmit={handleSubmit(onSubmit)} className={classes.form}>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="startDate"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <FormControl>
                <DatePicker
                  id="startDate"
                  label={t('startDate')}
                  invalidDateMessage={t('validation.date.invalid')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  disableFuture={false}
                  defaultValue={''}
                  placeholder={'00/00/0000'}
                  InputLabelProps={{
                    required: true,
                  }}
                  onAccept={() => {
                    trigger('startDate');
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
          <Controller
            name="endDate"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <FormControl variant="outlined">
                <DatePicker
                  id="endDate"
                  label={t('endDate')}
                  invalidDateMessage={t('validation.date.invalid')}
                  error={fieldState.invalid}
                  defaultValue={''}
                  disableFuture={false}
                  placeholder={'00/00/0000'}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  InputLabelProps={{
                    required: true,
                  }}
                  onAccept={() => {
                    trigger('endDate');
                  }}
                  {...fieldProps}
                />
              </FormControl>
            )}
          />
        </div>
      </div>
      <div className={classes.fieldLine}>
        <FormControl>
          <Controller
            name="stream"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState }) => (
              <SelectWithErrorField
                choices={streamChoices}
                choiceDisplayKey="name"
                selectLabel={t('stream')}
                InputLabelProps={{
                  required: true,
                }}
                disabled={!Boolean(streamChoices?.length)}
                placeholder={t('streamChoice')}
                error={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                inputRef={ref}
                {...inputProps}
              />
            )}
          />
        </FormControl>
      </div>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <FormControl>
            <Controller
              name="characterizationNumber"
              control={control}
              render={({ field: { ref, ...rest }, fieldState }) => (
                <IntegerField
                  id="characterizationNumber"
                  label={t('characterizationNumber')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  inputRef={ref}
                  placeholder={'0'}
                  InputLabelProps={{ required: true }}
                  {...rest}
                  onBlur={() => {
                    trigger(['characterizationNumber']);
                  }}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <Controller
              name="estimatedReceptionWeight"
              control={control}
              render={({ field: { ref, ...rest }, fieldState }) => (
                <WeightInputField
                  id="estimatedReceptionWeight"
                  label={t('estimatedReceptionWeight')}
                  inputRef={ref}
                  InputLabelProps={{
                    required: true,
                  }}
                  variant="outlined"
                  unit="t"
                  placeholder="0,000 t"
                  helperText={fieldState.error?.message}
                  error={fieldState.invalid}
                  {...rest}
                />
              )}
            />
          </FormControl>
        </div>
        <input
          type="hidden"
          {...register('client', {
            value: client['@id'],
          })}
        />
      </div>
    </form>
  );
};

export default SortingCenterContractForm;
